
import { useState } from 'react';
import { motion } from 'framer-motion';
import { simulatorRoutes, departures } from '../../../mocks/pricingData';

export default function QuickPriceGrid() {
  const [activeTab, setActiveTab] = useState(departures[0]);

  const routes = simulatorRoutes.filter((r) => r.departure === activeTab);

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-voyage-gold/10 rounded-full mb-4">
            <i className="ri-list-check-2 text-voyage-gold"></i>
            <span className="font-inter text-sm font-semibold text-voyage-gold">Grille tarifaire</span>
          </div>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-night-blue mb-3">
            Tous nos tarifs en un coup d&apos;œil
          </h2>
          <p className="font-inter text-slate-600 max-w-2xl mx-auto">
            Prix fixes par trajet, tout compris — WiFi, eau, assistance bagages inclus
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {departures.map((dep) => (
            <button
              key={dep}
              onClick={() => setActiveTab(dep)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-inter text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === dep
                  ? 'bg-gradient-to-r from-voyage-gold to-amber-500 text-night-blue shadow-lg scale-105'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:shadow-sm'
              }`}
            >
              <i className={`${dep.includes('Aéroport') ? 'ri-plane-line' : 'ri-train-line'} text-base`}></i>
              {dep.replace('Aéroport de ', '').replace('Gare de ', '')}
            </button>
          ))}
        </div>

        {/* Table */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden"
        >
          {/* Header */}
          <div className="grid grid-cols-12 bg-night-blue text-white">
            <div className="col-span-4 md:col-span-3 px-4 md:px-6 py-4">
              <span className="font-inter text-xs uppercase tracking-widest text-slate-300">Destination</span>
            </div>
            <div className="col-span-2 px-3 md:px-4 py-4 text-center hidden md:block">
              <span className="font-inter text-xs uppercase tracking-widest text-slate-300">Distance</span>
            </div>
            <div className="col-span-2 px-3 md:px-4 py-4 text-center hidden md:block">
              <span className="font-inter text-xs uppercase tracking-widest text-slate-300">Durée</span>
            </div>
            <div className="col-span-3 md:col-span-2 px-3 md:px-4 py-4 text-center">
              <span className="font-inter text-xs uppercase tracking-widest text-voyage-gold">Berline</span>
            </div>
            <div className="col-span-2 md:col-span-1 px-3 md:px-4 py-4 text-center">
              <span className="font-inter text-xs uppercase tracking-widest text-voyage-gold">Van</span>
            </div>
            <div className="col-span-3 md:col-span-2 px-3 md:px-4 py-4 text-center">
              <span className="font-inter text-xs uppercase tracking-widest text-slate-300">Réserver</span>
            </div>
          </div>

          {/* Rows */}
          {routes.map((route, idx) => (
            <div
              key={route.destination}
              className={`grid grid-cols-12 items-center ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-voyage-gold/5 transition-colors`}
            >
              <div className="col-span-4 md:col-span-3 px-4 md:px-6 py-4">
                <span className="font-inter text-sm font-semibold text-night-blue">{route.destination}</span>
              </div>
              <div className="col-span-2 px-3 md:px-4 py-4 text-center hidden md:block">
                <span className="font-inter text-sm text-slate-500">{route.distanceKm} km</span>
              </div>
              <div className="col-span-2 px-3 md:px-4 py-4 text-center hidden md:block">
                <span className="font-inter text-sm text-slate-500">~{route.durationMin} min</span>
              </div>
              <div className="col-span-3 md:col-span-2 px-3 md:px-4 py-4 text-center">
                <span className="font-inter text-sm font-bold text-night-blue">{route.priceBerline} €</span>
              </div>
              <div className="col-span-2 md:col-span-1 px-3 md:px-4 py-4 text-center">
                <span className="font-inter text-sm font-bold text-night-blue">{route.priceVan} €</span>
              </div>
              <div className="col-span-3 md:col-span-2 px-3 md:px-4 py-4 text-center">
                <a
                  href={`https://wa.me/33605606006?text=${encodeURIComponent(`Bonjour, je souhaite réserver un transfert de ${activeTab} vers ${route.destination}. Merci !`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-[#25D366] text-white rounded-lg font-inter text-xs font-semibold hover:bg-[#20BA5A] transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i>
                  <span className="hidden sm:inline">Réserver</span>
                </a>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Note */}
        <div className="mt-6 text-center">
          <p className="font-inter text-xs text-slate-400">
            * Prix indicatifs pour un aller simple. Aller-retour : -5% sur le total. Supplément nuit (22h-6h) : +30 €.
          </p>
        </div>
      </div>
    </section>
  );
}
